import React, { useEffect } from 'react';
import classNames from 'classnames';

import { ChevronDownIcon } from '@primer/octicons-react';

import { Tooltip } from '@primer/react';
import { getThemeColor } from '../../../utils/theme-utils';

import Button from '../Button/Button';
import Icon from '../icons/Icon';
import { InvisibleActionButton } from './InvisibleActionButton';

import CardHeader from '../Labels/CardHeader';
import ErrorLabel from '../Labels/ErrorLabel';

import './CardView.css';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

export const CollapsibleCardView = ({
  className,
  children,
  headerText,
  icon,
  quickActionClick, // TODO - To be replaced by quickActionsArray
  quickActionText, // TODO - To be replaced by quickActionsArray
  quickActionsArray = [],
  actionListArray,
  actionListOnSelect,
  fixedHeight,
  disabled,
  disabledText,
  subHeaderLabel,
  isLoading = false,
  loadingSpinnerKey = 'collapsible-card-view-spinner',
  quickActionVariant = 'primary',
  quickActionStyle = {},
  disableFullHeight = false,
  disablePadding = false,
  onStateChanged = () => {},
}) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const elemRef = React.useRef();
  const scrollHeight = React.useRef();
  // Padding top 16 + Padding bot 16 + Icon/CardHeader height 42
  const COLLAPSED_HEIGHT_PX = 74;

  // When collapsed state changes, send the state as an event
  useEffect(() => {
    if (onStateChanged) {
      onStateChanged(collapsed)
    }
  }, [collapsed, onStateChanged])

  const shrinkTo100 = () => {
    if (!elemRef.current) return;
    const elem = elemRef.current;
    const height = parseInt(elem.style.height.slice(0, -2), 10);
    // const width = parseInt(elem.style.width.slice(0, -2), 10);

    if (height > COLLAPSED_HEIGHT_PX) {
      const pace = Math.floor((scrollHeight.current[0] - COLLAPSED_HEIGHT_PX) / 60)
      elem.style.height = `${height - pace}px`;
      // elem.style.width = `${width - 10}px`;
      requestAnimationFrame(shrinkTo100)
    }
  }

  function collapseSection() {
    if (!elemRef.current) return;

    const element = elemRef.current;

    // get the height of the element's inner content, regardless of its actual size
    const sectionHeight = element.clientHeight;
    const sectionWidth = element.clientHeight;
    scrollHeight.current = [sectionHeight, sectionWidth];
    setCollapsed(true);

    // on the next frame (as soon as the previous style change has taken effect),
    // explicitly set the element's height to its current pixel height, so we 
    // aren't transitioning out of 'auto'
    requestAnimationFrame(() => {
      element.style.height = `${sectionHeight}px`;
      // element.style.width = `${sectionWidth}px`;

      // Begin the iterative shrink
      requestAnimationFrame(shrinkTo100);
    });
  }

  const growToScrollHeight = () => {
    if (!elemRef.current) return;
    const elem = elemRef.current;
    const height = parseInt(elem.style.height.slice(0, -2), 10);
    // const width = parseInt(elem.style.width.slice(0, -2), 10);

    if (height < scrollHeight.current[0]) {
      // The pace should roughly match the speed of the CSS transition in CardView.css
      const pace = Math.floor((scrollHeight.current[0] - COLLAPSED_HEIGHT_PX) / 60)
      elem.style.height = `${height + pace}px`;
      // elem.style.width = `${width + 10}px`;
      requestAnimationFrame(growToScrollHeight)
    } else if (!fixedHeight) {
      // Reset height back to auto.
      elem.style.height = '100%';
      // elem.style.width = '100%';
    }
  }

  function expandSection() {
    if (!elemRef.current) return;
    setCollapsed(false);

    requestAnimationFrame(growToScrollHeight);
  }

  return (
    <div
      ref={elemRef}
      className={classNames(
        'min-w-0 bg-white shadow border border-border-light rounded flex flex-col items-stretch justify-start overflow-hidden',
        { 'p-4': !disablePadding },
        className,
        (!disableFullHeight) && 'h-full w-full',
      )}
      style={fixedHeight !== undefined ? { height: fixedHeight } : {}}
    >
      {
        isLoading && (
          <LoadingSpinner
            animationKey={loadingSpinnerKey}
            useOverlay
          />
        )
      }
      {/* p-4 gives padding of 16px */}
      {
        headerText && (
          <div
            className={classNames(
              'px-4 mb-4 flex items-center justify-center',
              { 'mt-4': disablePadding },
            )}
          // This would give the best experience for mobile, but event propagation in ActionMenu seems broken.
          // onClick={() => collapsed ? expandSection() : collapseSection()}
          >
            <div className="flex flex-grow items-center">
              {icon && <Icon fill={getThemeColor('brand')} icon={icon} size={24} style={{ width: '24px', height: '24px' }} />}
              <CardHeader className="mx-2">{headerText}</CardHeader>
              {
                subHeaderLabel && (
                  <ErrorLabel>{subHeaderLabel}</ErrorLabel>
                )
              }
            </div>
            {
              quickActionsArray?.length > 0 && (
                quickActionsArray.map(({ label, disabledText, style = {}, variant = 'default', disabled = false, onClick = () => {} }) => (
                  disabled ? (
                      <Tooltip aria-label={disabledText} direction="sw" noDelay wrap>
                        <Button
                          variant={variant}
                          className="mx-2"
                          sx={style}
                          size="medium"
                          onClick={onClick}
                          disabled={disabled}
                        >{label}</Button>
                      </Tooltip>
                    )
                    : <Button
                      variant={variant}
                      className="mx-2"
                      sx={style}
                      size="medium"
                      onClick={onClick}
                      disabled={disabled}
                    >{label}</Button>
                ))
              )
            }
            {
              // TODO - Refactor uses of quickActionClick/Text -> as array props and remove this below
            }
            {
              (quickActionClick && quickActionText) ? (
                disabled ? (
                  <Tooltip aria-label={disabledText} direction="sw" noDelay wrap>
                    <Button
                      variant={quickActionVariant}
                      className="mx-2"
                      sx={quickActionStyle}
                      size="medium"
                      onClick={quickActionClick}
                      disabled={disabled}
                    >{quickActionText}</Button>
                  </Tooltip>
                )
                  : <Button
                    variant={quickActionVariant}
                    className="mx-2"
                    sx={quickActionStyle}
                    size="medium"
                    onClick={quickActionClick}
                    disabled={disabled}
                  >{quickActionText}</Button>
              ) : null
            }
            {actionListArray?.length > 0
              && <InvisibleActionButton
                actionListArray={actionListArray}
                actionListOnSelect={actionListOnSelect}
              />}
            <Button variant="invisible" onClick={() => collapsed ? expandSection() : collapseSection()}>
              <ChevronDownIcon fill={getThemeColor('brand')} size={24} className={classNames(collapsed ? 'chevron-down' : 'chevron-up')} />
            </Button>
          </div>
        )
      }
      <div className="px-4">{children}</div>
    </div>
  )
};


export default CollapsibleCardView;
